import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { LoadPanel } from 'devextreme-react/load-panel';
import { Toast } from 'devextreme-react/toast';

import Form, { Item, GroupItem, Label, RequiredRule } from 'devextreme-react/form';

import { Link, useHistory } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { faTag } from '@fortawesome/pro-duotone-svg-icons';

import { callProcesa } from '../api/callProcesa';

import './ClusterType.css';

export default function ClusterTypeForm(props) {
  const permissionLevel = props.nivel;
  const clusterId = props.clusterId;
  const isNew = !clusterId || clusterId === 'undefined';

  const history = useHistory();

  const [clusterTypePayload, setClusterTypePayload] = useState({});
  const [activateSaveButton, setActivateSaveButton] = useState(false);
  const [activateUpdateForm, setActivateUpdateForm] = useState(false);

  const [visibleLoadPanel, setVisibleLoadPanel] = useState(false);

  const [visibleToast, setVisibleToast] = useState(false);
  const [messageToast, setMessageToast] = useState('');
  const [typeToast, setTypeToast] = useState('success');

  const formDatosGeneralesRef = useRef(null);

  const internalidEditorOptions = useMemo(() => {
    return { width: '100%', readOnly: true, visible: false };
  }, []);

  const clusterTypeNameEditorOptions = useMemo(() => {
    return { width: '100%', inputAttr: { 'style': 'text-transform: uppercase' } };
  }, []);

  const toastPosition = useMemo(() => {
    return { my: 'top center', at: 'top center', of: window, offset: '0 130' };
  }, []);

  // Carga el payload según el id de la ruta (o inicializa nuevo).
  useEffect(() => {
    async function initialize() {
      setVisibleLoadPanel(true);
      try {
        if (isNew) {
          setClusterTypePayload({});
        } else {
          const datos = await callProcesa('/procesaclustertype', {
            action: 'payload',
            internalid: clusterId,
          });
          setClusterTypePayload(datos.data);
        }

        const canEdit = permissionLevel === 'Editar';
        setActivateUpdateForm(canEdit);
        setActivateSaveButton(canEdit);
      } catch (e) {
        if (e === 'No current user') {
          history.push('/');
          window.location.reload();
        }
      } finally {
        setVisibleLoadPanel(false);
      }
    }

    initialize();
  }, [clusterId, isNew, permissionLevel, history]);

  const saveButton = useCallback(async () => {
    if (formDatosGeneralesRef.current) {
      const formInstance = formDatosGeneralesRef.current.instance();
      const isValid = formInstance.validate();

      if (!isValid.isValid) {
        setVisibleToast(true);
        setMessageToast(isValid.brokenRules[0].message);
        setTypeToast('error');
        return;
      }
    }

    try {
      setVisibleLoadPanel(true);

      const payloadPost = await callProcesa('/procesaclustertype', {
        action: 'postData',
        internalid: clusterTypePayload.internalid,
        locationName: clusterTypePayload.name,
        data: clusterTypePayload,
      });

      if (payloadPost.success) {
        if (isNew) {
          setVisibleToast(true);
          setMessageToast('Se creó el Cluster Type con éxito');
          setTypeToast('success');

          // Navega al nuevo id: el contenedor re-monta el Form (key) y recarga.
          history.push('/ClusterType/' + payloadPost.data.id.toString());
        } else {
          setVisibleToast(true);
          setMessageToast('Se actualizó el Cluster Type con éxito');
          setTypeToast('success');

          // Refresca el payload con lo que devuelve NetSuite.
          const fresh = await callProcesa('/procesaclustertype', {
            action: 'payload',
            internalid: clusterTypePayload.internalid,
          });
          setClusterTypePayload(fresh.data);
        }
      } else {
        setVisibleToast(true);
        setMessageToast(payloadPost.message);
        setTypeToast('error');
      }

      setVisibleLoadPanel(false);
    } catch (e) {
      setVisibleLoadPanel(false);

      if (e === 'No current user') {
        history.push('/');
        window.location.reload();
      }
    }
  }, [clusterTypePayload, isNew, history]);

  const onHidingToast = useCallback(() => {
    setVisibleToast(false);
  }, []);

  return (
    <div>
      <div class="bg-gray-200 m-2">
        <h1 class="font-sans inline-block text-xl font-bold text-gray-700 leading-8 align-top mb-1 pl-3"><FontAwesomeIcon icon={faTag} size="xl" /> {clusterTypePayload.name}</h1>
      </div>

      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={visibleLoadPanel}
        showIndicator={true}
        message="Por favor espere..."
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
      />

      <Toast
        visible={visibleToast}
        message={messageToast}
        type={typeToast}
        onHiding={onHidingToast}
        displayTime={5000}
        width={'auto'}
        position={toastPosition}
      />

      <div class="m-3 bg-gray-200">
        <Link to="/ClusterType">
          <button type="submit" class="mb-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
            <FontAwesomeIcon icon={faArrowRotateLeft} /> Regresar
          </button>
        </Link>

        {activateSaveButton ? (
          <button onClick={saveButton} type="submit" class="ml-4 mb-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
            <FontAwesomeIcon icon={faSave} /> Guardar Cambios
          </button>
        ) : (
          <div class="inline-block"></div>
        )}

        <div class="mt-1 bg-white pt-3 shadow-xl">
          <div class="ml-4 mr-4 bg-white pt-3">
            <Form id="datosGenerales"
              labelMode={'outside'}
              formData={clusterTypePayload}
              readOnly={!activateUpdateForm}
              showColonAfterLabel={false}
              labelLocation={'left'}
              colCount={2}
              ref={formDatosGeneralesRef}
            >
              <GroupItem caption="Datos Generales">
                <Item cssClass="boldText" dataField="internalid" editorOptions={internalidEditorOptions} >
                  <Label text="" visible={false} />
                </Item>
                <Item cssClass="boldText" dataField="name" editorOptions={clusterTypeNameEditorOptions} >
                  <Label text={"Nombre"} />
                  <RequiredRule message="Nombre es requerido" />
                </Item>
                <Item dataField="isinactive" editorType="dxCheckBox" >
                  <Label text="Inactivo" />
                </Item>
              </GroupItem>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
